import { Plus } from "lucide-react";
import { useState } from "react";
import { CharacterDialog } from "@/shared/components/CharacterDialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { TableCell, TableRow } from "@/shared/components/ui/table";
import { RAID_ROLE_ICONS } from "@/shared/lib/roleIcons";
import {
  PERMISSION_ROLE_LABELS,
  type PermissionRole,
  permissionRoleColorStyle,
  type RaidRole,
} from "@/shared/lib/roles";
import { cn } from "@/shared/lib/utils";
import { classColorStyle } from "@/shared/lib/wowClass";
import { MAX_DISPLAY_NAME_LENGTH, type RowDraft } from "../roster.schemas";
import type { Character, RosterUser } from "../roster.types";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { ResetPasswordDialog } from "./ResetPasswordDialog";

export function RosterRow({
  user,
  draft,
  onDraftChange,
}: {
  user: RosterUser;
  draft: RowDraft;
  onDraftChange: (userId: string, next: RowDraft) => void;
}) {
  const update = (patch: Partial<RowDraft>) =>
    onDraftChange(user.id, { ...draft, ...patch });

  return (
    <TableRow>
      <TableCell className="whitespace-nowrap text-sm font-medium text-muted-foreground">
        {user.username}
      </TableCell>
      <TableCell>
        <Input
          value={draft.displayName}
          placeholder={user.username}
          maxLength={MAX_DISPLAY_NAME_LENGTH}
          onChange={(e) => update({ displayName: e.target.value })}
          className={cn("w-44", !user.wowClass && "text-muted-foreground")}
          style={classColorStyle(user.wowClass)}
        />
      </TableCell>

      <TableCell>
        <PermissionControl
          assignableRoles={user.assignablePermissionRoles}
          value={draft.permissionRole}
          onChange={(role) => update({ permissionRole: role })}
        />
      </TableCell>

      <TableCell>
        <CharactersCell user={user} />
      </TableCell>

      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          {user.canResetPassword && (
            <ResetPasswordDialog
              userId={user.id}
              name={draft.displayName || user.username}
            />
          )}
          {user.canDelete && (
            <DeleteUserDialog
              userId={user.id}
              name={draft.displayName || user.username}
            />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// Characters as class-coloured chips; a chip opens the edit dialog, the trailing + opens create.
function CharactersCell({ user }: { user: RosterUser }) {
  // null = closed; { character } = edit; {} = create.
  const [dialog, setDialog] = useState<{ character?: Character } | null>(null);

  return (
    <div className="flex flex-wrap items-center gap-1">
      {user.characters.map((c) => {
        // Main is marked by a class-coloured border (border-current picks up the
        // inline class colour); alts keep the neutral border.
        const RoleIcon = c.raidRole
          ? RAID_ROLE_ICONS[c.raidRole as RaidRole]
          : null;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => setDialog({ character: c })}
            className={cn(
              "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-sm hover:bg-accent",
              c.isMain ? "border-current" : "border-border",
            )}
            style={classColorStyle(c.wowClass)}
          >
            {RoleIcon && (
              <RoleIcon className="size-3 shrink-0 text-muted-foreground" />
            )}
            {c.name}
          </button>
        );
      })}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7 text-muted-foreground"
        aria-label="Lägg till karaktär"
        onClick={() => setDialog({})}
      >
        <Plus className="size-4" />
      </Button>
      <CharacterDialog
        open={dialog !== null}
        onOpenChange={(o) => !o && setDialog(null)}
        userId={user.id}
        character={dialog?.character}
      />
    </div>
  );
}

// Editability is server-driven: non-empty assignable list = editable Select, empty = read-only.
function PermissionControl({
  assignableRoles,
  value,
  onChange,
}: {
  assignableRoles: string[];
  value: PermissionRole;
  onChange: (role: PermissionRole) => void;
}) {
  if (assignableRoles.length > 0) {
    return (
      <Select
        // Applicant is never in the assignable list, so show the placeholder instead of a value.
        value={value === "applicant" ? undefined : value}
        onValueChange={(v) => onChange(v as PermissionRole)}
      >
        <SelectTrigger className="w-36">
          <SelectValue placeholder="Unassigned" />
        </SelectTrigger>
        <SelectContent>
          {assignableRoles.map((o) => (
            <SelectItem key={o} value={o}>
              <span style={permissionRoleColorStyle(o)}>
                {PERMISSION_ROLE_LABELS[o as PermissionRole]}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  // colorStyle is set for coloured ranks, undefined for applicant → fall back to muted text.
  const colorStyle = permissionRoleColorStyle(value);
  return (
    <span
      className={cn("px-1 text-sm", !colorStyle && "text-muted-foreground")}
      style={colorStyle}
    >
      {PERMISSION_ROLE_LABELS[value]}
    </span>
  );
}
